// The Waivers screen (WAIVERS.md): my claims in the order I want them
// tried, everyone on waivers with the clear time, and the transaction log
// with a sentence for every claim, won or lost. Phone: one region at a time
// behind segments. Tablet: my claims left, the wire and the log behind a
// segment on the right. Desktop: three columns.
//
// A REAL LEAGUE READS AND WRITES THE SERVER (W8): the view is
// `GET …/waivers`, and reordering, withdrawing and undoing are its writes,
// each answered in the server's own words and read back when it lands
// (`data/moves.ts`). The practice league is the simulator's, as it was.
//
// Drawn through the kit (LOOKS.md Part 7).

import { Fragment, useEffect, useState, type ReactNode } from 'react';
import type {
  ClaimOutcome,
  ClaimResultView,
  TransactionView,
  WaiverOrderRow,
  WaiverPlayerRow,
  WaiversView,
} from '@fsp/domain';
import { formatInZone } from '@fsp/domain';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLeagueBase, useReaderZone, useWaivers } from '../data/hooks';
import { useAtLeast } from '../data/detail';
import { useTools } from '../data/tools';
import { useLeagueSource } from '../data/leagueSource';
import { useActiveLeague } from '../data/LeaguesProvider';
import { FromLeague } from './shared/FromLeague';
import { Proj, projectionNote } from './shared/Projection';
import { ReverseMoveSheet, type ReverseTarget } from './shared/ReverseMoveSheet';
import {
  orderTitle,
  WaiverChangeSheet,
  WaiverOrderRows,
  WaiverOrderSheet,
} from './shared/WaiverOrderSheet';
import { LinkedText, PlayerName, playerSpans, TeamName } from './shared/Names';
import {
  EmptyState,
  GameLabel,
  positionLabel,
  PositionPill,
  ProTeamCode,
  StatusBadge,
  TeamMark,
} from '../kit';
import { Icon } from '../kit/Icon';
import { TeamFigure } from '../kit/TeamFigure';
import { Hint } from '../kit/overlays';
import {
  Button,
  Caption,
  Chip,
  Chips,
  Choice,
  Count,
  Cover,
  IconButton,
  Line,
  Meter,
  Segments,
  Stack,
  Text,
  TextField,
  Title,
  type Tone,
} from '../kit/ui';
import { sortRows } from '../kit/SortHead';
import { clubMatches, getSport } from '../sports/registry';
import '../styles/screens/waivers.css';
import { DetailSheet } from '../templates/DetailSheet';
import { SplitScreen, type SplitRegion } from '../templates/SplitScreen';
import { useTier } from '../shell/useTier';
import { num } from './shared/notComputed';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import { fmtWhen, ordinal, RosterMoveSheet, type RosterMove } from './shared/RosterMoveSheet';
import { logNotes, matchesFilter, type LogFilter } from './shared/waiversLog';
import { useWaiversLog } from '../data/waiversLog';
import { useWaiverWrites } from './shared/waiverWrites';
import { Term } from '../kit/Why';

const OUTCOME_LABEL: Record<ClaimOutcome, string> = {
  won: 'Won',
  'lost-bid': 'Outbid',
  'lost-priority': 'Lost on priority',
  'lost-tiebreak': 'Lost the tiebreak',
  'roster-full': 'No room',
  'over-budget': 'Over budget',
  'limit-reached': 'Limit reached',
  locked: 'Locked',
  gone: 'Gone',
  'skipped-conditional': 'Skipped',
  'season-over': 'Season over',
};

const OUTCOME_HEADLINE: Record<ClaimOutcome, (r: ClaimResultView) => string> = {
  won: (r) => `You got ${r.player.shortName}.`,
  'lost-bid': (r) => `Outbid. ${r.winningTeam?.name ?? 'Someone'} paid $${r.winningBid ?? 0}.`,
  'lost-priority': (r) =>
    `Beaten to it. ${r.winningTeam?.name ?? 'Someone'} had ${r.winnerRank ? ordinal(r.winnerRank) : 'better'} priority.`,
  'lost-tiebreak': () => 'Same bid, and the tiebreak went the other way.',
  'roster-full': () => 'No room on your roster.',
  'over-budget': () => 'Not enough budget left by then.',
  'limit-reached': () => 'You had used up your pickups.',
  locked: () => 'The game had already started.',
  gone: () => 'Someone got there first.',
  'skipped-conditional': () => 'Skipped: the claim it depended on won.',
  'season-over': () => 'The season ended before a run could decide it.',
};

function outcomeTone(o: ClaimOutcome): Tone {
  if (o === 'won') return 'good';
  if (o === 'skipped-conditional' || o === 'gone' || o === 'season-over') return 'neutral';
  return 'bad';
}

export function WaiversScreen() {
  return <FromLeague snapshot={useWaivers()}>{(view) => <WaiversBody view={view} />}</FromLeague>;
}

/** Now, a quarter-minute at a time: enough to take an Undo down when its five minutes are up. */
function useWallClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

function WaiversBody({ view }: { view: WaiversView }) {
  const real = useLeagueSource() === 'api';
  const { leagueId } = useParams();
  // The reader's zone (B-181). A real league's view is served in it already;
  // the simulator's is its own world's, so the reader's wins.
  const zone = useReaderZone() ?? view.timeZone;
  // A projection is a Standard number here (DETAIL_LEVELS §Waivers), on the
  // claim sheet this screen opens as on its rows (B-177).
  // And none in a league that keeps no projections (RULES_SCHEMA §15c).
  const standard = useAtLeast('standard');
  const tools = useTools();
  const showProjection = standard && tools.projections;
  const base = useLeagueBase();
  const navigate = useNavigate();
  const tier = useTier();
  const phone = tier === 'phone';
  const wall = useWallClock();
  // Reorder, withdraw and undo, shared with My Team's waivers panel (B-316).
  const { busy, moveClaim, claimToTop, withdraw, undo } = useWaiverWrites(view);
  // THE LOG BY ADDRESS (`?show=log`): the commissioner log's door to the
  // transaction log lands on it (the audit of 2026-10-17).
  const [params] = useSearchParams();
  const asked = params.get('show') === 'log' ? ('log' as const) : undefined;
  const [tabletRegion, setTabletRegion] = useState<'wire' | 'log'>(asked ?? 'wire');
  const [move, setMove] = useState<RosterMove | null>(null);
  const [card, setCard] = useState<PlayerCardData | null>(null);
  const [result, setResult] = useState<ClaimResultView | null>(null);
  const [filter, setFilter] = useState<LogFilter>('all');
  // The log past the view's newest hundred, read on asking (B-526).
  const fullLog = useWaiversLog(view, leagueId ?? null);
  const [wireQ, setWireQ] = useState('');
  const [wirePos, setWirePos] = useState('all');
  const [wireOrder, setWireOrder] = useState<WireOrder>('clears');
  const [reverse, setReverse] = useState<ReverseTarget | null>(null);
  // THE ORDER AND THE BUDGETS, for everybody; a commissioner changes one
  // team's place or budget from it (J7).
  const [orderOpen, setOrderOpen] = useState(false);
  // BY ID, READ FROM THE VIEW: a refusal reads the view again, and the sheet
  // then says what the team has now, not what it had when Change was tapped.
  const [changing, setChanging] = useState<string | null>(null);
  const changingRow: WaiverOrderRow | null =
    changing === null ? null : (view.order.find((r) => r.team.id === changing) ?? null);
  // The commissioner's Change on a team's row, from the sheet or the page's own list.
  const changeTeam =
    real && view.commissioner !== null
      ? (row: WaiverOrderRow) => {
          setOrderOpen(false);
          setChanging(row.team.id);
        }
      : null;
  // A COMMISSIONER REVERSES A REAL LEAGUE'S MOVES (J2); the practice league
  // has no commissioner to do it.
  const active = useActiveLeague();
  const canReverse = real && active?.league.isCommissioner === true;
  const reverseOf = (tx: TransactionView): ReverseTarget | null =>
    tx.reversed || tx.kind === 'commissioner'
      ? null
      : {
          transactionId: tx.id,
          kind: tx.kind,
          teamName: tx.team.name,
          added: tx.added,
          dropped: tx.dropped,
          bid: tx.faab,
        };
  const reverseClaim = (r: ClaimResultView): ReverseTarget | null =>
    r.outcome !== 'won' || r.transactionId === null || r.reversed
      ? null
      : {
          transactionId: r.transactionId,
          kind: 'claim',
          teamName: r.team.name,
          added: [r.player],
          dropped: r.drops,
          bid: r.bid,
        };

  const openCard = (r: WaiverPlayerRow) => {
    const kind = r.action === 'none' ? null : r.action;
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      points: null,
      statLine: null,
      action: kind
        ? {
            label: r.myClaimId ? 'Edit claim' : kind === 'add' ? 'Add' : 'Claim',
            onClick: () => {
              setCard(null);
              setMove({ kind, playerId: r.player.id, claimId: r.myClaimId });
            },
          }
        : null,
    });
  };

  /**
   * Whether a move can still be undone. The practice league works it out on
   * its own clock and says so with the field; a real league's field is the
   * instant the five minutes end, which passes while the screen is open.
   */
  const undoable = (tx: TransactionView) =>
    tx.undoableUntil !== null && (!real || Date.parse(tx.undoableUntil) > wall);

  // ── Header ──────────────────────────────────────────────────────────────
  const status = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>Waivers</Title>
        <div className="fsp-sub fsp-ui-wrap-text">
          {view.headline}
          {view.processesAt ? ` Next: ${fmtRun(view.processesAt, zone)}.` : ''}
        </div>
      </div>
      <div className="fsp-waivers-meters">
        {view.budget ? (
          <Hint label={`$${view.budget.left} left of $${view.budget.total}`}>
            <div className="fsp-budget">
              <Caption>Budget</Caption>
              <div className="fsp-num fsp-waivers-figure">
                ${view.budget.left}
                <span className="fsp-muted fsp-waivers-of"> of ${view.budget.total}</span>
              </div>
              <Meter value={Math.max(0, view.budget.left) / Math.max(1, view.budget.total)} />
            </div>
          </Hint>
        ) : null}
        {view.priority ? (
          <Hint label={view.priority.why}>
            <div className="fsp-budget">
              <Caption>Priority</Caption>
              <div className="fsp-num fsp-waivers-figure">
                {ordinal(view.priority.rank)}
                <span className="fsp-muted fsp-waivers-of"> of {view.priority.of}</span>
                <span className="fsp-waivers-info">
                  <Icon name="info" size="sm" />
                </span>
              </div>
            </div>
          </Hint>
        ) : null}
        {view.order.length > 0 ? (
          <Button size="xs" variant="quiet" onClick={() => setOrderOpen(true)}>
            {view.orderSource === null ? 'Budgets' : 'Order'}
          </Button>
        ) : null}
        <Button size="xs" variant="quiet" onClick={() => navigate(`${base}/rules`)}>
          Rules
        </Button>
      </div>
    </div>
  );

  // ── My claims ───────────────────────────────────────────────────────────
  const claims = (
    <div className="fsp-waivers-list">
      {view.myClaims.length === 0 ? (
        <EmptyState>
          No claims yet. The waiver wire waits for no one, except you, until{' '}
          {view.processesAt ? fmtWhen(view.processesAt, zone, true) : 'processing'}.
        </EmptyState>
      ) : null}
      {view.myClaims.map((c, i) => (
        <div
          key={c.id}
          className={`fsp-surface fsp-claim-card ${c.problem ? 'fsp-row-problem' : ''}`}
        >
          <div className="fsp-claim-head">
            <Term
              className="fsp-claim-order fsp-num"
              tip={`Tried ${ordinal(c.order)} of your claims.`}
              name={String(c.order)}
            >
              {c.order}
            </Term>
            <PositionPill pos={c.player.positions[0]!} size="sm" />
            <div className="fsp-waivers-grow">
              <div className="fsp-name">
                {/* A button, as My Team's waivers panel has it, so a key or a
                    screen reader opens the card too (the audit of 2026-10-17). */}
                <button
                  type="button"
                  className="fsp-linklike fsp-name-text fsp-name-btn"
                  onClick={() =>
                    setCard({
                      player: c.player,
                      game: c.game,
                      projection: c.projection,
                      points: null,
                      statLine: null,
                      action: null,
                    })
                  }
                >
                  {c.player.name}
                </button>
                <ProTeamCode code={c.player.proTeam} />
                <StatusBadge player={c.player} />
              </div>
              <div className="fsp-sub">
                <GameLabel game={c.game} />
              </div>
            </div>
            <div className="fsp-claim-arrows">
              {/* FIRST IN ONE PRESS (B-380): the fifth claim to the top was
                  four presses of the arrow. */}
              {i > 1 ? (
                <Button
                  size="xs"
                  variant="quiet"
                  label={`Try ${c.player.name} first`}
                  disabled={busy}
                  onClick={() => claimToTop(c.id)}
                >
                  To top
                </Button>
              ) : null}
              <IconButton
                icon="up"
                label="Try earlier"
                variant="quiet"
                disabled={i === 0 || busy}
                onClick={() => moveClaim(c.id, -1)}
              />
              <IconButton
                icon="down"
                label="Try later"
                variant="quiet"
                disabled={i === view.myClaims.length - 1 || busy}
                onClick={() => moveClaim(c.id, 1)}
              />
            </div>
          </div>
          <div className="fsp-claim-terms">
            {c.bid !== null ? (
              <span className="fsp-num fsp-waivers-figure">${c.bid}</span>
            ) : (
              <span>Priority claim</span>
            )}
            <span className="fsp-muted">·</span>
            <span>
              {c.drops.length
                ? [
                    'Drop ',
                    ...c.drops.map((d, i) => (
                      <Fragment key={d.id}>
                        {i > 0 ? ', ' : ''}
                        <PlayerName player={d} club={d.proTeam}>
                          {d.shortName}
                        </PlayerName>
                      </Fragment>
                    )),
                  ]
                : 'No drop'}
            </span>
            {c.conditionalOn ? (
              <>
                <span className="fsp-muted">·</span>
                <span>Only if {c.conditionalOn.playerName} fails</span>
              </>
            ) : null}
            {/* The pool's projection for him, at Standard and above, as
                DETAIL_LEVELS gives Waivers its projection (B-177). The claim
                carried it and only the card it opened showed it (round 3's
                review, SCREENS-9). */}
            {showProjection ? (
              <>
                <span className="fsp-muted">·</span>
                <span className="fsp-num">proj {num(c.projection)}</span>
              </>
            ) : null}
          </div>
          <div
            className={`fsp-sub fsp-ui-wrap-text fsp-claim-standing ${c.problem ? 'problem' : ''}`}
          >
            {c.standing}
            {c.processesAt && !c.problem ? ` Processes ${fmtWhen(c.processesAt, zone)}.` : ''}
          </div>
          <Line gap={2}>
            <Button
              size="xs"
              onClick={() => setMove({ kind: 'claim', playerId: c.player.id, claimId: c.id })}
            >
              Edit
            </Button>
            <Button size="xs" variant="danger-quiet" disabled={busy} onClick={() => withdraw(c)}>
              Withdraw
            </Button>
          </Line>
        </div>
      ))}
    </div>
  );

  // ── On waivers ──────────────────────────────────────────────────────────
  // FIND ONE ON THE WIRE (B-380): a name or a club, a position, and an
  // order. The list is the server's, clearing soonest; the rest is a look at
  // it. An order by a figure is offered only where the figure is on the row
  // (Standard), so the order never rests on a number nobody can see.
  const sportOrder = active
    ? getSport(active.league.sport as never).positions.map((p) => p.id)
    : [];
  const wirePositions = [...new Set(view.onWaivers.map((r) => r.player.positions[0] ?? ''))]
    .filter((p) => p !== '')
    .sort((a, b) => {
      const [x, y] = [sportOrder.indexOf(a), sportOrder.indexOf(b)];
      return (x < 0 ? 99 : x) - (y < 0 ? 99 : y) || a.localeCompare(b);
    });
  // A position the wire no longer has, or a search with its box gone, is
  // no filter: one that stayed would empty the list with no way back.
  const pos = wirePositions.includes(wirePos) ? wirePos : 'all';
  const filtersShown = view.onWaivers.length > 1 || wireQ.trim() !== '';
  const needle = filtersShown ? wireQ.trim().toLowerCase() : '';
  const order: WireOrder =
    (!standard && wireOrder !== 'name') || (!showProjection && wireOrder === 'projection')
      ? 'clears'
      : wireOrder;
  const filtered = view.onWaivers.filter(
    (r) =>
      (pos === 'all' || r.player.positions.includes(pos)) &&
      (needle === '' ||
        r.player.name.toLowerCase().includes(needle) ||
        clubMatches(r.player.proTeam, needle)),
  );
  const wireRows =
    order === 'name'
      ? [...filtered].sort((a, b) => a.player.name.localeCompare(b.player.name))
      : sortRows(
          filtered,
          order === 'projection'
            ? (r) => r.projection
            : order === 'average'
              ? (r) => r.seasonAvg
              : null,
          'high',
          false,
        );
  const wireFilters = filtersShown ? (
    <div className="fsp-wire-filters">
      <Line gap={2}>
        <span className="fsp-wire-search">
          <TextField
            label="Search the wire"
            hideLabel
            placeholder="Name or club"
            icon="search"
            value={wireQ}
            onChange={setWireQ}
          />
        </span>
        <span className="fsp-wire-order">
          <Choice
            label="Order"
            hideLabel
            searchable={false}
            required
            value={order}
            onChange={(v) => v && setWireOrder(v as WireOrder)}
            options={[
              { value: 'clears', label: 'Off waivers soonest' },
              ...(showProjection ? [{ value: 'projection', label: 'This week’s projection' }] : []),
              ...(standard ? [{ value: 'average', label: 'Points a game' }] : []),
              { value: 'name', label: 'Name' },
            ]}
          />
        </span>
      </Line>
      {wirePositions.length > 1 ? (
        <div className="fsp-chip-row">
          <Chips
            label="Position"
            single
            options={[
              { value: 'all', label: 'All' },
              ...wirePositions.map((p) => ({ value: p, label: positionLabel(p) })),
            ]}
            value={[pos]}
            onChange={(v) => setWirePos(v[0] ?? 'all')}
          />
        </div>
      ) : null}
    </div>
  ) : null;
  const wire = (
    <div>
      {wireFilters}
      {view.onWaivers.length > 0 && wireRows.length === 0 ? (
        <EmptyState>
          {view.onWaiversTruncated
            ? 'Nobody among the ones clearing soonest matches that. Players has everyone on waivers.'
            : 'Nobody on the wire matches that.'}
        </EmptyState>
      ) : null}
      {view.onWaivers.length === 0 ? (
        <EmptyState>
          Nobody is on waivers right now. Everyone dropped is already fair game.
        </EmptyState>
      ) : null}
      {view.onWaiversTruncated ? (
        // B-143: after the draft every undrafted player is on waivers, and the
        // list carries the ones clearing soonest. Said at the top, where the
        // list starts, so nobody scrolls a hundred rows to find out.
        <div className="fsp-sub fsp-ui-wrap-text fsp-waivers-truncated">
          <span className="fsp-waivers-grow">
            The {view.onWaivers.length} clearing soonest. More are on waivers, and Players lists
            them all.
          </span>
          {/* It keeps its width: the sentence wraps instead (it was cut to
              "Playe" at every width, the review of W8). */}
          <Button size="xs" onClick={() => navigate(`${base}/players`)}>
            Players
          </Button>
        </div>
      ) : null}
      {wireRows.map((r) => (
        <div
          key={r.player.id}
          className={`fsp-row fsp-row-clickable fsp-wire-row ${standard ? 'projected' : ''}`}
          // THE NAME IS THE BUTTON; THE ROW IS A BIGGER TARGET FOR IT (B-275),
          // as on Players: a keyboard and a screen reader reach the card
          // through the name.
          data-ux-convenience=""
          onClick={() => openCard(r)}
        >
          <PositionPill pos={r.player.positions[0]!} size="sm" />
          <div className="fsp-waivers-who">
            <div className="fsp-name">
              <button
                type="button"
                className="fsp-linklike fsp-name-text fsp-name-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openCard(r);
                }}
              >
                {r.player.name}
              </button>
              <ProTeamCode code={r.player.proTeam} />
              <StatusBadge player={r.player} />
            </div>
            <div className="fsp-sub fsp-ui-wrap-text">
              {r.cleared ? (
                <span>Free agent since {fmtWhen(r.clearsAt, zone)}</span>
              ) : (
                <span className="fsp-wire-clears">
                  On waivers until {fmtWhen(r.clearsAt, zone)}
                </span>
              )}
              {r.droppedBy ? <span> · by {r.droppedBy}</span> : null}
              {r.reason ? <span className="fsp-muted"> · {r.reason}</span> : null}
              {/* WHY HIS PROJECTION IS 0 OR NONE (B-521), as Players says it:
                  a bare 0.0 read as a number, not as "Out". */}
              {showProjection &&
              !r.player.onBye &&
              projectionNote(r.projection, r.basis) !== null ? (
                <span className="fsp-muted"> · {projectionNote(r.projection, r.basis)}</span>
              ) : null}
            </div>
          </div>
          {/* The projection is a Standard number on Waivers (DETAIL_LEVELS
              §Waivers): Simple removes it rather than printing one on every
              row (round 3's review, SCREENS-8). */}
          {/* His season stays in a league that keeps no projections (§15c),
              which still orders the wire by it. */}
          {standard ? (
            <div className="fsp-right fsp-num fsp-wire-proj">
              {showProjection ? <Proj>{num(r.projection)}</Proj> : null}
              {/* HIS SEASON BESIDE HIS WEEK (B-380), as a claim weighs it. */}
              <div className="fsp-sub fsp-wire-avg">
                {num(r.seasonAvg)}
                <span className="fsp-proj-tag">avg</span>
              </div>
            </div>
          ) : null}
          {/* Keeps the row's own tap off the action's (data-ux-stop: not a control). */}
          <div data-ux-stop="" onClick={(e) => e.stopPropagation()}>
            {r.myClaimId ? (
              <Button
                size="xs"
                full
                onClick={() =>
                  setMove({ kind: 'claim', playerId: r.player.id, claimId: r.myClaimId })
                }
              >
                Claimed
              </Button>
            ) : r.action !== 'none' ? (
              <Button
                size="xs"
                full
                // A row's action, tinted: a page of claims was a page of
                // filled buttons (B-278, UX-15).
                variant={r.action === 'claim' ? 'caution-soft' : 'soft'}
                onClick={() =>
                  setMove({ kind: r.action as 'add' | 'claim', playerId: r.player.id })
                }
              >
                {r.action === 'claim' ? 'Claim' : 'Add'}
              </Button>
            ) : r.reason === null ? (
              // The practice league's lock, which has no sentence of its own.
              <Text tone="muted" size="xs">
                locked
              </Text>
            ) : // A reason is under the name already — no team, moves closed,
            // the season over (B-147, B-152) — and "locked" beside it was
            // wrong for every one of them (the review of W8).
            null}
          </div>
        </div>
      ))}
    </div>
  );

  // ── Log ─────────────────────────────────────────────────────────────────
  // THE FILTER IS THE SCREEN'S, over every entry read so far: Older entries
  // reads on through the whole log whatever the chips say, so a filter that
  // finds nothing in the newest hundred can look further back (B-526).
  const entries = fullLog.entries.filter((e) => matchesFilter(e, filter));
  const notes = logNotes(filter, entries.length, fullLog.entries.length, fullLog.more);
  // A LITTLE DANCE FOR A CLAIM I WON (PLAN-2026-10-31-a-handshake-a-dance-and-
  // the-photo §2): my mark on it, in the newest run only, so a season of wins
  // does not all dance at once.
  const newestRun = fullLog.entries.find((e) => e.kind === 'run');
  const newestRunId = newestRun?.kind === 'run' ? newestRun.run.id : null;
  const dances = (r: ClaimResultView) =>
    wonByMe(r) &&
    newestRun?.kind === 'run' &&
    newestRun.run.results.some((x) => x.claimId === r.claimId);
  const log = (
    <div>
      <div className="fsp-pinned fsp-log-filters fsp-chip-row">
        <Chips
          label="Which moves"
          single
          options={[
            { value: 'all', label: 'All' },
            { value: 'mine', label: 'Mine' },
            { value: 'adds', label: 'Adds' },
            { value: 'drops', label: 'Drops' },
            { value: 'claims', label: 'Claims' },
          ]}
          value={[filter]}
          onChange={(v) => setFilter((v[0] as LogFilter | undefined) ?? 'all')}
        />
      </div>
      {notes.empty ? <EmptyState>{notes.empty}</EmptyState> : null}
      <div className="fsp-waivers-list">
        {entries.map((e) =>
          e.kind === 'run' ? (
            <div key={e.run.id} className="fsp-surface fsp-log-run">
              <div className="fsp-log-run-head">
                <Text weight="bold">{fmtRun(e.run.at, zone)}</Text>
                <span className="fsp-sub fsp-ui-wrap-text">{e.run.summary}</span>
              </div>
              {e.run.results
                .filter((r) => filter !== 'mine' || r.isMine)
                .map((r) => (
                  <div
                    key={r.claimId}
                    className={`fsp-row fsp-row-clickable fsp-log-row fsp-cover-host ${r.isMine ? 'fsp-row-selected' : ''}`}
                  >
                    {/* The row opens the claim's result; the names in it open
                        the player's card and the team, above that door (B-220). */}
                    <Cover
                      label={`${r.team.name}: the claim's result`}
                      onClick={() => setResult(r)}
                    />
                    <TeamMark
                      team={r.team}
                      size={24}
                      face={e.run.id === newestRunId && dances(r) ? 'dancing' : 'idle'}
                    />
                    <div className="fsp-waivers-who">
                      <div className="fsp-log-line">
                        <LinkedText
                          text={r.sentence}
                          names={playerSpans(r.sentence, [r.player, ...r.drops])}
                        />
                      </div>
                      <div className="fsp-sub">
                        <TeamName team={r.team} />
                        {/* Every team's bid, once the run is over (B-136). */}
                        {r.bid !== null ? ` · bid $${r.bid}` : ''}
                        {r.reversed ? ' · reversed by the commissioner' : ''}
                      </div>
                    </div>
                    <Chip tone={outcomeTone(r.outcome)}>{OUTCOME_LABEL[r.outcome]}</Chip>
                  </div>
                ))}
            </div>
          ) : (
            <div
              key={e.transaction.id}
              className={`fsp-surface fsp-row fsp-log-row fsp-log-move ${e.transaction.team.isMine ? 'fsp-row-selected' : ''}`}
            >
              <TeamMark team={e.transaction.team} size={24} />
              <div className="fsp-waivers-who">
                <div className={`fsp-log-line ${e.transaction.reversed ? 'undone' : ''}`}>
                  <LinkedText
                    text={e.transaction.summary}
                    names={playerSpans(e.transaction.summary, [
                      ...e.transaction.added,
                      ...e.transaction.dropped,
                    ])}
                  />
                </div>
                <div className="fsp-sub">
                  <TeamName team={e.transaction.team} />
                  {' · '}
                  {fmtWhen(e.transaction.at, zone)}
                  {e.transaction.reversal === 'commissioner'
                    ? ' · reversed by the commissioner'
                    : e.transaction.reversed
                      ? ' · undone'
                      : ''}
                </div>
                {/* A commissioner's reversal says why, in their words (J2). */}
                {e.transaction.kind === 'commissioner' && e.transaction.reason ? (
                  <div className="fsp-sub fsp-ui-wrap-text">“{e.transaction.reason}”</div>
                ) : null}
              </div>
              {undoable(e.transaction) ? (
                <Button
                  size="xs"
                  variant="quiet"
                  icon="undo"
                  disabled={busy}
                  onClick={() => undo(e.transaction)}
                >
                  Undo
                </Button>
              ) : null}
              {canReverse && reverseOf(e.transaction) !== null ? (
                // AN ICON, NOT A WORD: the log is a narrow column beside two
                // others on a desktop, and a commissioner's tool should not
                // take every line's width from the managers who read it.
                <IconButton
                  icon="undo"
                  label="Reverse this move"
                  variant="quiet"
                  size="sm"
                  onClick={() => setReverse(reverseOf(e.transaction))}
                />
              ) : null}
            </div>
          ),
        )}
        {notes.note ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-log-note">{notes.note}</div>
        ) : null}
        {fullLog.failure !== null ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-log-note">{fullLog.failure}</div>
        ) : null}
        {fullLog.more ? (
          <div className="fsp-log-older">
            <Button
              size="xs"
              variant="quiet"
              disabled={fullLog.loadingOlder}
              onClick={() => fullLog.older?.()}
            >
              {fullLog.loadingOlder ? 'Reading…' : 'Older entries'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );

  const regions: SplitRegion[] = [
    {
      id: 'claims',
      title: 'My claims',
      content: claims,
      badge: view.myClaims.length ? <Count value={view.myClaims.length} tone="warn" /> : undefined,
    },
    { id: 'wire', title: 'On waivers', content: wire },
    { id: 'log', title: 'Log', content: log },
  ];

  const [claimsRegion, ...rightRegions] = regions;
  const right = rightRegions.find((r) => r.id === tabletRegion) ?? rightRegions[0]!;

  return (
    <>
      {phone ? (
        <SplitScreen
          status={status}
          phoneDefault={view.myClaims.length ? 'claims' : 'wire'}
          show={asked}
          regions={regions}
        />
      ) : (
        // TWO COLUMNS, NEVER THREE (DIRECTION U21, B-319): claims left, and
        // the wire and the log behind a segment on the right, from a tablet up.
        // A desktop had the three side by side, a wall of lists.
        // WAIVERS.md: "Tablet: Claims left, On waivers right; Log behind a
        // segment." Three columns at 834 squeezed every row to a third of a
        // tablet; the log is a place to look back, and a segment away.
        <div className="fsp-screen">
          <div className="fsp-pinned">{status}</div>
          <div className="fsp-regions fsp-split-5-5">
            <section className="fsp-region" aria-label={claimsRegion!.title}>
              <div className="fsp-region-head">
                <span className="fsp-cap">{claimsRegion!.title}</span>
                {claimsRegion!.badge}
              </div>
              {claimsRegion!.content}
              {/* EVERY TEAM'S PLACE AND BUDGET under my claims (B-391), as
                  ESPN and Sleeper keep them on the waivers page: the column
                  was one empty sentence while the order sat behind a link. */}
              {view.order.length > 0 ? (
                <>
                  <div className="fsp-region-head fsp-waivers-order-head">
                    <span className="fsp-cap">{orderTitle(view)}</span>
                  </div>
                  <WaiverOrderRows view={view} onChange={changeTeam} withSource />
                </>
              ) : null}
            </section>
            <section className="fsp-region" aria-label={right.title}>
              <div className="fsp-region-head">
                <span className="fsp-grow">
                  <Segments
                    label="On waivers or the log"
                    items={rightRegions.map((r) => ({
                      id: r.id as 'wire' | 'log',
                      label: r.title,
                    }))}
                    value={right.id as 'wire' | 'log'}
                    onChange={setTabletRegion}
                  />
                </span>
              </div>
              {right.content}
            </section>
          </div>
        </div>
      )}
      <RosterMoveSheet move={move} showProjection={showProjection} onClose={() => setMove(null)} />
      <PlayerCard data={card} onClose={() => setCard(null)} />
      <ResultSheet
        result={result}
        dancing={result !== null && dances(result)}
        onClose={() => setResult(null)}
        onReverse={
          canReverse && result !== null && reverseClaim(result) !== null
            ? () => {
                setReverse(reverseClaim(result));
                setResult(null);
              }
            : null
        }
      />
      {leagueId !== undefined ? (
        <ReverseMoveSheet target={reverse} leagueId={leagueId} onClose={() => setReverse(null)} />
      ) : null}
      <WaiverOrderSheet
        view={view}
        opened={orderOpen}
        onClose={() => setOrderOpen(false)}
        onChange={changeTeam}
      />
      {leagueId !== undefined ? (
        <WaiverChangeSheet
          view={view}
          row={changingRow}
          leagueId={leagueId}
          onClose={() => setChanging(null)}
        />
      ) : null}
    </>
  );
}

/** The wire's orders (B-380): the server's own, a figure's, or the name. */
type WireOrder = 'clears' | 'projection' | 'average' | 'name';

function fmtRun(iso: string, timeZone: string): string {
  return formatInZone(iso, timeZone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** A claim of mine that went through and stands: the one a team's character dances over. */
function wonByMe(r: ClaimResultView): boolean {
  return r.isMine && r.outcome === 'won' && !r.reversed;
}

/** One claim's result in full: the headline for me, the sentence, and the facts behind it. */
function ResultSheet({
  result,
  dancing,
  onClose,
  onReverse,
}: {
  result: ClaimResultView | null;
  /** A claim of mine won in the newest run: my character dances beside it. */
  dancing: boolean;
  onClose: () => void;
  /** The commissioner's way to take a won claim back (J2); null when it cannot be. */
  onReverse: (() => void) | null;
}) {
  const r = result;
  return (
    <DetailSheet opened={r !== null} onClose={onClose} title={r ? 'Claim result' : undefined}>
      {r ? (
        <Stack gap={4}>
          <Line gap={3} align="start">
            <PositionPill pos={r.player.positions[0]!} />
            <div className="fsp-card-who">
              <div className="fsp-disp fsp-card-name">
                {r.isMine
                  ? OUTCOME_HEADLINE[r.outcome](r)
                  : `${r.team.name}: ${OUTCOME_LABEL[r.outcome]}`}
              </div>
              <Text block>{r.sentence}</Text>
            </div>
            {dancing ? (
              <TeamFigure team={r.team} mood="dancing" height={72} className="fsp-result-dance" />
            ) : null}
          </Line>
          <div className="fsp-surface fsp-result-facts">
            <Fact label="Player" value={<PlayerName player={r.player} club={r.player.proTeam} />} />
            {r.bid !== null ? (
              <Fact label={r.isMine ? 'Your bid' : 'Bid'} value={`$${r.bid}`} />
            ) : null}
            {r.winningBid !== null && r.outcome !== 'won' ? (
              <Fact label="Winning bid" value={`$${r.winningBid}`} />
            ) : null}
            {r.winningTeam && r.outcome !== 'won' ? (
              <Fact label="Went to" value={<TeamName team={r.winningTeam} />} />
            ) : null}
            {r.priorityRank !== null &&
            r.winnerRank !== null &&
            (r.outcome === 'lost-priority' || r.outcome === 'lost-tiebreak') ? (
              <Fact
                label="Priority"
                value={`${r.team.name} ${ordinal(r.priorityRank)} · ${r.winningTeam?.name ?? 'winner'} ${ordinal(r.winnerRank)}`}
              />
            ) : null}
            {r.drops.length ? (
              <Fact
                label={r.outcome === 'won' ? 'Dropped' : 'Would have dropped'}
                value={r.drops.map((d, i) => (
                  <Fragment key={d.id}>
                    {i > 0 ? ', ' : ''}
                    <PlayerName player={d} club={d.proTeam} />
                  </Fragment>
                ))}
              />
            ) : null}
            <Chip tone={outcomeTone(r.outcome)}>{OUTCOME_LABEL[r.outcome]}</Chip>
          </div>
          {r.reversed ? <Text block>The commissioner has reversed this claim.</Text> : null}
          {onReverse !== null ? (
            <Button variant="secondary" icon="undo" onClick={onReverse}>
              Reverse this claim
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </DetailSheet>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="fsp-fact">
      <span className="fsp-cap">{label}</span>
      <span>{value}</span>
    </div>
  );
}
